import axiosServices from '../utils/axios';
const backendUrl = import.meta.env.VITE_BACKEND_URL;

export const getWorkspaces = async () => {
  const response = await axiosServices.get(`${backendUrl}/workspaces`);
  return response.data;
};

export const createWorkspace = async (payload) => {
  const response = await axiosServices.post(`${backendUrl}/workspaces`, payload);
  return response.data;
};

export const getWorkspace = async (workspaceId, token) => {
  const url = token
    ? `${backendUrl}/workspaces/${workspaceId}?token=${token}`
    : `${backendUrl}/workspaces/${workspaceId}`;

  const response = await axiosServices.get(url);
  return response.data;
};

export const updateWorkspace = async (workspaceId, payload) => {
  const response = await axiosServices.put(`${backendUrl}/workspaces/${workspaceId}`, payload);
  return response.data;
};

export const updateWorkspaceSettings = async (workspaceId, settingsPayload) => {
  const response = await axiosServices.patch(
    `${backendUrl}/workspaces/${workspaceId}/settings`,
    settingsPayload,
  );
  return response.data;
};

export const deleteWorkspace = async (workspaceId) => {
  const response = await axiosServices.delete(`${backendUrl}/workspaces/${workspaceId}`);
  return response.data;
};

export const listMembers = async (workspaceId) => {
  const response = await axiosServices.get(`${backendUrl}/workspaces/${workspaceId}/members`);
  return response.data;
};

export const getWorkspaceMembers = async (workspaceId) => {
  const response = await axiosServices.get(`${backendUrl}/workspaces/${workspaceId}/members`);

  return response.data;
};

export const inviteMember = async (workspaceId, payload) => {
  const response = await axiosServices.post(
    `${backendUrl}/workspaces/${workspaceId}/invite`,
    payload,
  );
  return response.data;
};

export const acceptInvitation = async (workspaceId, token) => {
  const response = await axiosServices.post(
    `${backendUrl}/workspaces/${workspaceId}/accept?token=${token}`,
  );
  return response.data;
};

export const declineInvitation = async (workspaceId, token, notificationId) => {
  const params = new URLSearchParams({ token });
  if (notificationId) {
    params.append('notification_id', notificationId);
  }
  const response = await axiosServices.post(
    `${backendUrl}/workspaces/${workspaceId}/decline?${params.toString()}`,
  );
  return response.data;
};

/**
 * Auto-join a workspace using an invitation token.
 * This endpoint automatically accepts the invitation and adds the user to the workspace.
 * @param {string} workspaceId - The workspace ID
 * @param {string} token - The invitation token
 * @returns {Promise<Object>} - Response with workspace details and success status
 */
export const joinWorkspace = async (workspaceId, token) => {
  const response = await axiosServices.get(
    `${backendUrl}/workspaces/${workspaceId}/join?token=${token}`,
  );
  return response.data;
};

export const removeMember = async (workspaceId, email) => {
  const encodedEmail = encodeURIComponent(email);
  const response = await axiosServices.delete(
    `${backendUrl}/workspaces/${workspaceId}/members/${encodedEmail}`,
  );
  return response.data;
};

export const revokeInvitation = async (workspaceId, invitationId) => {
  const response = await axiosServices.delete(
    `${backendUrl}/workspaces/${workspaceId}/invitations/${invitationId}`,
  );
  return response.data;
};

export const revokePendingSignupInvite = async (workspaceId, inviteId) => {
  const response = await axiosServices.delete(
    `${backendUrl}/workspaces/${workspaceId}/pending-signup-invites/${inviteId}`,
  );
  return response.data;
};

export const reassignRole = async (workspaceId, payload) => {
  const response = await axiosServices.put(
    `${backendUrl}/workspaces/${workspaceId}/update`,
    payload,
  );
  return response.data;
};

/**
 * Get workspace usage and entitlement data.
 * Returns entitlement balances, caps, expiration info, and account representative.
 * For legacy workspaces, returns { legacy: true, message: "..." }
 */
export const getWorkspaceUsage = async (workspaceId) => {
  const response = await axiosServices.get(`${backendUrl}/workspaces/${workspaceId}/usage`);
  return response.data;
};

/**
 * Get workspace transaction history (audit log of entitlement changes).
 * Only accessible to workspace owners and admins.
 * @param {string} workspaceId - The workspace ID
 * @param {Object} options - Optional query params
 * @param {number} options.limit - Max records to return (default 100)
 * @param {number} options.offset - Records to skip (default 0)
 * @param {string} options.entitlement_type - Filter by type (optional)
 */
export const getWorkspaceTransactions = async (workspaceId, options = {}) => {
  const params = new URLSearchParams();
  if (options.limit) params.append('limit', options.limit);
  if (options.offset) params.append('offset', options.offset);
  if (options.entitlement_type) params.append('entitlement_type', options.entitlement_type);

  const queryString = params.toString();
  const url = `${backendUrl}/workspaces/${workspaceId}/transactions${queryString ? `?${queryString}` : ''}`;

  const response = await axiosServices.get(url);
  return response.data;
};

/**
 * Get aggregated workspace statistics.
 * Returns study counts, response counts, AI participant data, and recent activity.
 * @param {string} workspaceId - The workspace ID
 * @returns {Promise<Object>} - Aggregated statistics for the workspace
 */
export const getWorkspaceStatistics = async (workspaceId) => {
  const response = await axiosServices.get(`${backendUrl}/workspaces/${workspaceId}/statistics`);
  return response.data;
};
